<template>
  <div class="gallery-pack relative" :class="pack.pack_type">
    <header class="flexing font-bold text-48 text-texts-standard-default uppercase">
      {{ $replacePlaceholder($t('gallery.typePacks'), '{type}', $t(`gallery.${pack.pack_type}Pack`)) }}
    </header>

    <main class="flex justify-center relative">
      <div class="gallery-pack-img absolute" />
    </main>

    <footer class="absolute bottom-0 flex items-center flex-col justify-evenly">
      <p class="text-28 text-texts-standard-default">{{ $t('gallery.packContains') }}</p>

      <div class="gallery-pack-box relative flexing" :class="$isSsm ? 'revert-skew' : 'skew'">
        <section class="flexing w-full" :class="{ 'revert-skew': $isWsm }">
          <p class="text-36 text-texts-standard-important mr-1 font-bold">{{ pack.total_pieces }}</p>

          <app-icon icon-name="puzzle-52" />

          <tippy
            theme="WSM2"
            placement="right"
            max-width="35rem"
            class="gallery-pack-box-info absolute"
          >
            <app-icon icon-name="info-50" />

            <template #content>
              <gallery-pack-tooltip
                :quantity="pack.total_pieces"
                :pieces="pack.pack_pieces"
                :title="tooltipText"
              />
            </template>
          </tippy>
        </section>
      </div>

      <div class="gallery-pack-count flex justify-between items-center pl-8 pr-6">
        <p class="uppercase text-texts-standard-default text-30">{{ $t('gallery.yourPacks') }}</p>

        <div class="flexing">
          <p
            class="text-48 font-bold"
            :class="hasPack ? 'text-texts-standard-important' : 'text-texts-standard-danger'"
          >
            {{ pack.amount }}
          </p>

          <app-main-icon :icon-size="52" :icon-name="`gallery-pack-${pack.pack_type}`" />
        </div>
      </div>

      <div class="gallery-pack-buttons flex justify-evenly w-full">
        <app-button
          v-if="hasPack"
          class="gallery-pack-open-btn"
          btn-id="gallery-pack-open-btn"
          btn-type="primary"
          :btn-text="$t('gallery.openPack')"
          btn-size="md"
          @click="openThisPack"
        />

        <app-button
          v-if="hasPack"
          class="gallery-pack-open-btn"
          btn-id="gallery-pack-open-all-btn"
          btn-type="secondary"
          :btn-text="$t('gallery.openAll')"
          btn-size="md"
          @click="openAllPacks"
        />

        <app-button
          v-else
          class="gallery-pack-premium-open-btn"
          btn-id="gallery-pack-premium-open-btn"
          btn-type="credit"
          :btn-text="btnText"
          btn-size="md"
          @click="goToPremium"
        />
      </div>
    </footer>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { PropType } from 'vue'
import GalleryPackTooltip from './GalleryPackTooltip.vue'
import type { GalleryPack } from '@/interfaces/gallery/Packs'
import { useGalleryPacksStore } from '@/store/pinia/gallery/packsStore'
import { mapActions } from 'pinia'

export default defineComponent({
  name: 'GalleryPack',

  components: {
    GalleryPackTooltip,
  },

  props: {
    pack: {
      type: Object as PropType<GalleryPack>,
      required: true
    },
  },

  computed: {
    tooltipText(): string {
      return this.$replacePlaceholder(
        this.$t('gallery.typePack'),
        '{type}',
        this.$t(`gallery.${this.pack.pack_type}Pack`),
      )
    },

    btnText(): string {
      return this.$replacePlaceholder(
        this.$t('gallery.buyTypePacks'),
        '{type}',
        this.$t(`gallery.${this.pack.pack_type}Pack`)
      )
    },

    hasPack(): boolean {
      return this.pack.amount > 0
    }
  },

  methods: {
    ...mapActions(useGalleryPacksStore, ['loadOpenedPacks', 'openPack']),

    openThisPack() {
      this.openPack(this.pack.shop_items_id)
    },

    openAllPacks() {
      this.openPack(this.pack.shop_items_id, this.pack.amount)
    },

    goToPremium(): void {
      this.$router.push({ name: this.$getWebView('PremiumGalleryPieces') })
    },
  },
})
</script>

<style lang="scss" scoped>
$types: ('regular', 'premium', 'jumbo');
.gallery-pack {
  width: 36.75rem;
  height: 46.5rem;
  @each $type in $types {
    &.#{$type} {
      header {
        @include background(
          url('#{$path-gallery}packs/gallery-pack-header-#{$type}.avif'),
          contain
        );
        width: 36.75rem;
        height: 5.938rem;
      }
      main {
        @include background(url('#{$path-gallery}packs/gallery-pack-body-#{$type}.avif'), contain);
        width: 36.75rem;
        height: 40.563rem;
        .gallery-pack-img {
          @include background(url('#{$path-gallery}packs/gallery-pack-img-#{$type}.avif'), contain);
          width: 27.813rem;
          height: 29.813rem;
          top: 0.25rem;
        }
      }
      footer {
        @include background(
          url('#{$path-gallery}packs/gallery-pack-footer-#{$type}.avif'),
          contain
        );
        width: 36.75rem;
        height: 17.625rem;
      }
      .gallery-pack-count {
        width: 38.688rem;
        height: 4rem;

        @if $type == 'regular' {
          @if $isWsm {
            @include background(
              url('#{$path-gallery}packs/gallery-pack-stripe-#{$type}.avif'),
              contain
            );
          }
          @if $isSsm {
            border-style: solid;
            border-width: 0.188rem;
            border-image-slice: 1;
            background: linear-gradient(
              90deg,
              rgba(55, 55, 55, 0) 0%,
              rgba(55, 55, 55, 0.6) 10%,
              rgba(55, 55, 55, 0.6) 90%,
              rgba(55, 55, 55, 0) 100%
            );
            border-image-source: linear-gradient(
              90deg,
              rgba(141, 141, 141, 0) 0%,
              rgba(141, 141, 141, 1) 10%,
              rgba(141, 141, 141, 1) 90%,
              rgba(141, 141, 141, 0) 100%
            );
          }
        }
        @if $type == 'premium' {
          @if $isWsm {
            @include background(
              url('#{$path-gallery}packs/gallery-pack-stripe-#{$type}.avif'),
              contain
            );
          }
          @if $isSsm {
            border-style: solid;
            border-width: 0.188rem;
            border-image-slice: 1;
            background: linear-gradient(
              90deg,
              rgba(95, 62, 8, 0) 0%,
              rgba(95, 62, 8, 0.6) 10%,
              rgba(95, 62, 8, 0.6) 90%,
              rgba(55, 55, 55, 0) 100%,
              rgba(95, 62, 8, 0) 100%
            );
            border-image-source: linear-gradient(
              90deg,
              rgba(184, 152, 98, 0) 0%,
              rgba(184, 152, 98, 1) 10%,
              rgba(184, 152, 98, 1) 90%,
              rgba(55, 55, 55, 0) 100%,
              rgba(184, 152, 98, 1) 100%
            );
          }
        }
        @if $type == 'jumbo' {
          @if $isWsm {
            @include background(
              url('#{$path-gallery}packs/gallery-pack-stripe-#{$type}.avif'),
              contain
            );
          }
          @if $isSsm {
            border-style: solid;
            border-width: 0.188rem;
            border-image-slice: 1;
            background: linear-gradient(
              90deg,
              rgba(55, 55, 55, 0) 0%,
              rgba(55, 55, 55, 0.6) 10%,
              rgba(55, 55, 55, 0.6) 90%,
              rgba(55, 55, 55, 0) 100%
            );
            border-image-source: linear-gradient(
              90deg,
              rgba(54, 17, 83, 0) 0%,
              rgba(54, 17, 83, 0.6) 10%,
              rgba(54, 17, 83, 0.6) 90%,
              rgba(55, 55, 55, 0) 100%,
              rgba(54, 17, 83, 0) 100%
            );
          }
        }
      }
    }
  }
  &-box {
    width: 16.25rem;
    height: 4rem;
    border-style: solid;
    border-width: 0.125rem;
    border-image-slice: 1;
    @if $isSsm {
      background-color: #363e5d;
      border-image-source: linear-gradient(to top, #fff284, #f6c717);
      box-shadow:
        inset -0.188rem 0 2.5rem 0 rgba(6, 34, 55, 0.48),
        inset -0.0188rem -0.188rem 2.5rem 0 rgba(6, 34, 55, 0.48);
    }
    @if $isWsm {
      background-color: #0f3251;
      border-image-source: linear-gradient(to top, #fff284, #f6c717);
      box-shadow:
        inset -0.188rem 0 2.5rem 0 rgba(51, 123, 214, 0.19),
        inset -0.0188rem -0.188rem 2.5rem 0 rgba(6, 34, 55, 0.48);
    }
    &-info {
      top: 0.275rem;
      right: 0.275rem;
    }
  }
  &-open-btn {
    width: 17.438rem;
  }
  &-premium-open-btn {
    width: 35.5rem;
  }
}
</style>
